/**
 * Fehlerberichte aus dem Browser (0158): Abstürze und unbehandelte Fehler gehen gedrosselt an public.report_client_error.
 * Die Datenbank entfernt Persönliches und bündelt; hier wird nur vorgefiltert, was kein Fehler der App ist (Erweiterungen,
 * schlechtes Netz, Abbrüche), und je Sitzung begrenzt. Kein fremder Dienst, kein zusätzliches Skript.
 */

import { maskSecrets } from "@talentkontor/domain";
export type FehlerArt = "error" | "rejection" | "render" | "chunk";

export interface Bericht {
  kind: FehlerArt;
  portal: "agentur" | "creator" | "betrieb" | "oeffentlich" | "konto";
  message: string;
  source?: string;
  stack?: string;
  path: string;
  release: string;
  browser: string;
  org_id?: string;
  session: string;
}

type Senden = (b: Bericht) => Promise<unknown>;

const MAX_JE_SITZUNG = 10;
const GLEICHER_FEHLER_MS = 10 * 60_000;

let senden: Senden | null = null;
let release = "dev";
let agentur: string | undefined;
let gesendet = 0;
const zuletzt = new Map<string, number>();

/** Zufällige Kennung dieser Seitensitzung: zählt Betroffene ohne Anmeldung, ohne etwas über die Person zu wissen. */
const sitzung = (() => {
  try {
    const a = new Uint8Array(12);
    crypto.getRandomValues(a);
    return Array.from(a, (b) => b.toString(36).padStart(2, "0")).join("").slice(0, 20);
  } catch {
    return "ohne-zufall";
  }
})();

/** Agentur des sichtbaren Arbeitsbereichs; die Datenbank übernimmt sie nur, wenn die Person dazugehört. */
export function setFehlerAgentur(orgId: string | undefined): void {
  agentur = orgId;
}
/** Agentur des sichtbaren Arbeitsbereichs (für „Melden“, 0176) */
export function fehlerAgentur(): string | undefined {
  return agentur;
}

export function portalVon(pfad: string): Bericht["portal"] {
  const teil = pfad.split("/")[1] ?? "";
  if (teil === "agentur") return "agentur";
  if (teil === "creator") return "creator";
  if (teil === "betrieb") return "betrieb";
  if (teil === "konto" || teil === "anmelden" || teil === "einrichtung") return "konto";
  return "oeffentlich";
}

/** „Chrome 129 / macOS“: genug zum Nachstellen, kein vollständiger Fingerabdruck des Geräts. */
export function browserKurz(ua: string): string {
  const os = /iPhone|iPad/.test(ua) ? "iOS" : /Android/.test(ua) ? "Android" : /Mac OS X/.test(ua) ? "macOS" : /Windows/.test(ua) ? "Windows" : /Linux/.test(ua) ? "Linux" : "?";
  // Reihenfolge zählt: Edge und Opera nennen auch „Chrome“, Chrome nennt auch „Safari“
  const namen: Array<[RegExp, string]> = [[/Edg\/(\d+)/, "Edge"], [/OPR\/(\d+)/, "Opera"], [/(?:Firefox|FxiOS)\/(\d+)/, "Firefox"],
    [/(?:Chrome|CriOS)\/(\d+)/, "Chrome"], [/Version\/(\d+).*Safari/, "Safari"]];
  for (const [re, name] of namen) {
    const m = re.exec(ua);
    if (m) return `${name} ${m[1]} / ${os}`;
  }
  return `Browser / ${os}`;
}

/** Was kein Fehler der App ist: Netz weg, Abbruch, Erweiterungen, bekannte harmlose Browsermeldungen. */
export function istRauschen(message: string, source?: string): boolean {
  if (!message || message === "Script error." || message === "Script error") return true;
  if (/ResizeObserver loop/i.test(message)) return true;
  // Link-Scanner von Microsoft (Outlook/Defender „Safe Links“) öffnen Links aus Mails in einem Windows-Chrome und werfen diese
  // Meldung aus ihrem eigenen Skript; kein Fehler der App (Betriebsbericht 09.10.2026, /registrieren aus Akquise-Mails)
  if (/^Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+$/.test(message)) return true;
  if (/^(AbortError|The (user|operation) (aborted|was aborted))|signal is aborted/i.test(message)) return true;
  if (/Failed to fetch$|NetworkError when attempting|Load failed$|Network request failed|ERR_NETWORK|net::ERR_|The Internet connection appears to be offline/i.test(message)) return true;
  if (source && /^(chrome|moz|safari|safari-web)-extension:/.test(source)) return true;
  if (source && /^https?:/.test(source)) {
    try {
      if (new URL(source).origin !== location.origin) return true;
    } catch {
      /* unlesbare Quelle: melden */
    }
  }
  return false;
}

/** Laden eines Programmteils gescheitert (meist alte Seite nach einem Update). */
export function istLadefehler(message: string): boolean {
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|ChunkLoadError/i.test(message);
}

function nachricht(e: unknown): { message: string; stack?: string } {
  if (e instanceof Error) return { message: `${e.name && e.name !== "Error" ? `${e.name}: ` : ""}${e.message}`.slice(0, 500), stack: e.stack?.slice(0, 4000) };
  if (typeof e === "string") return { message: e.slice(0, 500) };
  try {
    return { message: JSON.stringify(e).slice(0, 500) };
  } catch {
    return { message: String(e).slice(0, 500) };
  }
}

/** Erste Zeile des Stacks mit Datei aus der eigenen App (für den Fingerabdruck). */
function quelleAusStack(stack?: string): string | undefined {
  return stack?.split("\n").map((l) => /(https?:\/\/[^\s)]+:\d+:\d+)/.exec(l)?.[1]).find(Boolean);
}

export function meldeFehler(kind: FehlerArt, fehler: unknown, quelle?: string): void {
  if (!senden || gesendet >= MAX_JE_SITZUNG) return;
  // 0181: Schlüssel, Tokens und Zahlungsdaten schon im Browser entfernen (die Datenbank bereinigt zusätzlich)
  const roh = nachricht(fehler);
  const message = maskSecrets(roh.message);
  const stack = roh.stack ? maskSecrets(roh.stack) : undefined;
  const source = quelle ?? quelleAusStack(stack);
  if (istRauschen(message, source)) return;
  const schluessel = `${kind}|${message.replace(/\d+/g, "0")}|${source?.replace(/:\d+:\d+$/, "") ?? ""}`;
  const jetzt = Date.now();
  if ((zuletzt.get(schluessel) ?? 0) > jetzt - GLEICHER_FEHLER_MS) return;
  zuletzt.set(schluessel, jetzt);
  gesendet++;
  const bericht: Bericht = {
    kind, portal: portalVon(location.pathname), message, source, stack, path: location.pathname, release,
    browser: browserKurz(navigator.userAgent), org_id: agentur, session: sitzung,
  };
  // Ein Fehler beim Melden darf nie einen weiteren Bericht auslösen
  void senden(bericht).catch(() => undefined);
}

const NEU_GELADEN = "co-neu-geladen";

/**
 * Einmal neu laden, wenn ein Programmteil nach einem Update fehlt. Innerhalb von 5 Minuten kein zweites Mal (keine
 * Schleife): dann false, der Fehler läuft weiter bis zur Fehlerseite und wird dort gemeldet.
 */
export function neuLadenNachUpdate(): boolean {
  try {
    const vorher = Number(sessionStorage.getItem(NEU_GELADEN) ?? 0);
    if (Date.now() - vorher > 5 * 60_000) {
      sessionStorage.setItem(NEU_GELADEN, String(Date.now()));
      location.reload();
      return true;
    }
  } catch {
    /* ohne Speicher: nicht neu laden, damit keine Schleife entsteht */
  }
  return false;
}

/** Einmal beim Start: globale Fänger für Fehler, unbehandelte Promises und fehlende Programmteile. */
export function setupFehlerbericht(opts: { senden: Senden; release: string; aktiv: boolean }): void {
  if (!opts.aktiv || senden) return;
  senden = opts.senden;
  release = opts.release;
  window.addEventListener("error", (ev) => {
    // Fehler beim Laden von Bildern/Skripten (ev.target ist ein Element) sind kein Absturz
    if (!(ev instanceof ErrorEvent)) return;
    meldeFehler("error", ev.error ?? ev.message, ev.filename || undefined);
  });
  window.addEventListener("unhandledrejection", (ev) => meldeFehler("rejection", ev.reason));
  window.addEventListener("vite:preloadError", (ev) => {
    if (neuLadenNachUpdate()) ev.preventDefault();
  });
}
